import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

function Header() {
  const { user, isAuthenticated, isElevated, logout } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [switching, setSwitching] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const response = await authAPI.getDependents();
      if (response.success) {
        const allAccounts = [
          { 
            userId: user.userId, 
            displayName: user.displayName || user.username, 
            accountType: user.accountType || 'primary',
            isCurrent: true
          },
          ...response.data.dependents.map(d => ({ 
            ...d, 
            accountType: 'dependent',
            isCurrent: false
          }))
        ];
        setAccounts(allAccounts);
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadAccounts();
    }
  }, [isAuthenticated, user, loadAccounts]);

  const handleSwitchAccount = async (targetUserId) => {
    if (targetUserId === user?.userId) return; // Already on this account
    
    setSwitching(true);
    try {
      const response = await authAPI.switchAccount(targetUserId);
      if (response.success) {
        // Store new token and reload immediately
        localStorage.setItem('token', response.data.token);
        // Force immediate reload
        window.location.reload();
        return; // This line won't execute, but good practice
      } else {
        setSwitching(false);
        alert('Failed to switch account: ' + (response.error?.message || 'Unknown error'));
      }
    } catch (error) {
      setSwitching(false);
      console.error('Switch account error:', error);
      alert('Failed to switch account: ' + (error.response?.data?.error?.message || error.message));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="header">
      <div className="header-content">
        <h1 onClick={() => navigate('/')}>🏇 ARFTCHC</h1>
        
        {isAuthenticated && (
          <>
            <nav className="nav">
              <ul>
                <li><Link to="/">Dashboard</Link></li>
                <li><Link to="/races">Races</Link></li>
                <li><Link to="/my-picks">Picks</Link></li>
                <li><Link to="/results">Results</Link></li>
                <li><Link to="/tidbits">Tidbits</Link></li>
                <li><Link to="/chat">Chat</Link></li>
                <li><Link to="/leaderboard">Leaders</Link></li>
                <li><Link to="/help" title="Help & Support">❓</Link></li>
                {isElevated && (
                  <li><Link to="/admin" className="admin-link">⚙️</Link></li>
                )}
              </ul>
            </nav>
            
            <div className="user-info">
              {accounts.length > 1 && (
                <select 
                  className="account-switcher"
                  value={user?.userId} 
                  onChange={(e) => handleSwitchAccount(e.target.value)}
                  disabled={switching}
                  title="Switch Account"
                >
                  {accounts.map(acc => (
                    <option key={acc.userId} value={acc.userId}>
                      {acc.accountType === 'dependent' ? '👶' : '👤'} {acc.displayName}
                    </option>
                  ))}
                </select>
              )}
              <span className="welcome-text">Hi, {user?.displayName || user?.username}</span>
              <Link to="/account" className="change-pw-link" title="Account Settings">⚙️</Link>
              <button onClick={handleLogout} className="logout-button">
                Logout
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}

export default Header;
